import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Download, Home } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const SunburstChart = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState(['All TRM']);
  const svgRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (data && svgRef.current) {
      drawSunburst();
    }
  }, [data]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/trm/products`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();
      processData(result.products || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processData = (products) => {
    // Build hierarchy: Root → Categories → Manufacturers → Products
    const hierarchy = {
      name: 'All TRM',
      children: []
    };

    const categoryMap = {};

    products.forEach(p => {
      // Get or create category
      if (!categoryMap[p.trm_category]) {
        categoryMap[p.trm_category] = {
          name: p.trm_category,
          children: []
        };
        hierarchy.children.push(categoryMap[p.trm_category]);
      }

      const category = categoryMap[p.trm_category];

      // Find or create manufacturer
      let manufacturer = category.children.find(m => m.name === p.manufacturer_name);
      if (!manufacturer) {
        manufacturer = {
          name: p.manufacturer_name,
          children: []
        };
        category.children.push(manufacturer);
      }

      // Add product
      manufacturer.children.push({
        name: p.canonical_name,
        value: p.version_count || 1,
        product_id: p.product_id,
        status: p.record_status
      });
    });

    setData(hierarchy);
  };

  const drawSunburst = () => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const container = containerRef.current;
    const size = Math.min(container.clientWidth, 700);
    const radius = size / 2;

    svg.attr('width', size)
       .attr('height', size)
       .attr('viewBox', `${-size / 2} ${-size / 2} ${size} ${size}`);

    // Color scale
    const color = d3.scaleOrdinal()
      .domain(['Database', 'DevOps', 'Security', 'Collaboration', 'ITSM', 'Monitoring', 'Networking', 'Cloud Infrastructure', 'Analytics', 'Automation'])
      .range(d3.schemeTableau10);

    // Partition layout
    const partition = d3.partition()
      .size([2 * Math.PI, radius]);

    // Hierarchy
    const root = d3.hierarchy(data)
      .sum(d => d.value || 0)
      .sort((a, b) => b.value - a.value);

    partition(root);

    // Arc generator
    const arc = d3.arc()
      .startAngle(d => d.x0)
      .endAngle(d => d.x1)
      .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius / 2)
      .innerRadius(d => d.y0)
      .outerRadius(d => d.y1 - 1);

    // Tooltip
    const tooltip = d3.select('body').append('div')
      .attr('class', 'sunburst-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', 'rgba(0, 0, 0, 0.9)')
      .style('color', 'white')
      .style('padding', '12px')
      .style('border-radius', '8px')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '10000')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)');

    // Draw arcs
    const paths = svg.append('g')
      .selectAll('path')
      .data(root.descendants().filter(d => d.depth > 0))
      .enter()
      .append('path')
      .attr('d', arc)
      .attr('fill', d => {
        while (d.depth > 1) d = d.parent;
        return color(d.data.name);
      })
      .attr('fill-opacity', d => {
        if (d.depth === 1) return 0.8;
        if (d.depth === 2) return 0.6;
        return 0.4;
      })
      .attr('stroke', 'white')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('fill-opacity', 1)
          .attr('stroke', '#3b82f6')
          .attr('stroke-width', 3);

        const path = d.ancestors().reverse().map(node => node.data.name).join(' → ');
        const percentage = ((d.value / root.value) * 100).toFixed(1);
        
        tooltip.style('visibility', 'visible')
          .html(`
            <div style="font-size: 11px; color: var(--cares-blue); margin-bottom: 6px;">${path}</div>
            <div style="font-weight: 700; font-size: 16px; margin-bottom: 4px;">${d.data.name}</div>
            <div style="color: var(--cares-blue); font-size: 14px;">${d.value} versions</div>
            <div style="color: var(--cares-text-note); font-size: 12px; margin-top: 4px;">${percentage}% of total</div>
          `);
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 15) + 'px');
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .attr('fill-opacity', d => {
            if (d.depth === 1) return 0.8;
            if (d.depth === 2) return 0.6;
            return 0.4;
          })
          .attr('stroke', 'white')
          .attr('stroke-width', 2);
        tooltip.style('visibility', 'hidden');
      })
      .on('click', (event, d) => {
        event.stopPropagation();
        const path = d.ancestors().reverse().map(node => node.data.name);
        setBreadcrumb(path);
        zoomTo(d);
      });

    // Zoom function
    const zoomTo = (p) => {
      root.each(d => {
        d.target = {
          x0: Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
          x1: Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
          y0: Math.max(0, d.y0 - p.y0),
          y1: Math.max(0, d.y1 - p.y0)
        };
      });

      const t = svg.transition().duration(750);

      paths.transition(t)
        .tween('data', d => {
          const i = d3.interpolate(d.current, d.target);
          return t => d.current = i(t);
        })
        .attrTween('d', d => () => arc(d.current));
    };

    // Initialize current state
    root.each(d => d.current = {
      x0: d.x0,
      x1: d.x1,
      y0: d.y0,
      y1: d.y1
    });

    // Center label
    svg.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('font-size', '20px')
      .attr('font-weight', 'bold')
      .attr('fill', '#1f2937')
      .text('TRM Portfolio');

    svg.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('y', 25)
      .attr('font-size', '14px')
      .attr('fill', '#6b7280')
      .text(`${root.value} versions`);

    return () => {
      tooltip.remove();
    };
  };

  const resetZoom = () => {
    setBreadcrumb(['All TRM']);
    if (data && svgRef.current) {
      drawSunburst();
    }
  };

  const exportSVG = () => {
    const svgElement = svgRef.current;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'trm-sunburst.svg';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading sunburst...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
              TRM Sunburst Chart
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
              Hierarchical view: Categories → Manufacturers → Products
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={resetZoom} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: 'var(--cares-surface-raised)', color: 'var(--cares-text-max)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
              <Home size={16} />
              Reset
            </button>
            <button onClick={exportSVG} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: 'var(--cares-blue)', color: 'var(--cares-text-max)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div style={{ padding: '12px 16px', background: 'var(--cares-surface-board)', borderRadius: '8px', fontSize: '14px', color: 'var(--cares-text-label)' }}>
          {breadcrumb.join(' → ')}
        </div>
      </div>

      {/* Visualization */}
      <div ref={containerRef} style={{ background: 'var(--cares-surface-card)', borderRadius: '12px', padding: '40px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'center' }}>
        <svg ref={svgRef}></svg>
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '24px', padding: '16px', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', borderRadius: '8px', fontSize: '13px', color: 'var(--cares-blue)' }}>
        <strong>💡 Tip:</strong> Click on any segment to zoom in. Use the Reset button to return to the full view.
      </div>
    </div>
  );
};

export default SunburstChart;
